"use client";

import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Header from "../../layouts/Header";
import Toaster from "../../components/ui/toaster";
import { TransactionHistoryAsset } from "../../components/transaction-history-asset";
import { useAuth } from "../../context/AuthContext";
import {
  useFetchAllTradablePairs,
  useMarketAssets,
  usePriceHistory,
  usePortfolioEstimateAssets,
  useWalletAssets,
  useUserTransactionHistory,
} from "../../hooks/query/queries";
import { usePairStore } from "../../store/useStore";
import coinMarketCap from "../../assets/images/assetdashboard/coinMarketCap.png";
import CurrencyFormatter from "../../utils/currencyFormatter";
import {
  formatMarketCap,
  formatVolume,
  formatSupply,
  formatPrice,
  formatPercentOfATH,
} from "../../utils/marketDataFormatter";
import {
  truncateDecimals,
  formatWithThousandSpaces,
} from "../../utils/numberFormatter";
import { Skeleton } from "../../components/ui/skeleton";
import TradingInterface from "../../components/trading-interface";
import { Button } from "../../components/ui/button";
import alertIcon from "../../assets/images/assetdashboard/alert.svg";
import depositIcon from "../../assets/images/assetdashboard/deposit.svg";
import { ToggleGroup, ToggleGroupItem } from "../../components/ui/toggle-group";
import fundsIcon from "../../assets/images/trading/flow.svg";
import sendCrypto from "../../assets/images/sentCrypto.png";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from "chart.js";
import dayjs from "dayjs";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler
);

const AssetPill = ({ assetSymbol, image, displayValue }) => {
  const [imageError, setImageError] = useState(false);

  return (
    <div
      className="flex-shrink-0 h-[32px] bg-white rounded-full px-3 flex items-center gap-2 border border-[#EBE9EF]"
      style={{ minWidth: "fit-content" }}
    >
      <div className="w-[16px] h-[16px] rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center">
        {image && !imageError ? (
          <img
            src={image}
            alt={assetSymbol}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400 rounded-full">
            <span className="text-xs font-semibold">
              {assetSymbol?.charAt(0) || "?"}
            </span>
          </div>
        )}
      </div>
      <span className="text-sm font-semibold text-[#6D6584] whitespace-nowrap">
        {displayValue}
      </span>
    </div>
  );
};

export default function AssetDashboard() {
  const { settings } = useAuth();
  const timeFilters = ["1D", "1W", "1M", "6M", "1Y"];
  const location = useLocation();
  const navigate = useNavigate();
  const selectedAsset = location.state;
  const { data: marketAssets } = useMarketAssets();
  const { userProfile } = useAuth();
  const { setPairObject } = usePairStore();
  const { data: pairObject } = useFetchAllTradablePairs();
  const userId = userProfile?._id;
  const { data: walletAssetsData, isLoading: isLoadingWallet } =
    useWalletAssets();
  const { data: portfolioEstimateData, isLoading: isLoadingEstimate } =
    usePortfolioEstimateAssets();
  const { data: transactionsComplete, isLoading: isLoadingTransactions } =
    useUserTransactionHistory(userId);
  const formatter = useMemo(() => new CurrencyFormatter(), []);
  const getDefaultTimeFilter = useCallback(() => {
    const period = settings?.preferences?.performancePeriod?.toLowerCase();
    switch (period) {
      case "daily":
        return "1D";
      case "weekly":
        return "1W";
      case "monthly":
        return "1M";
      default:
        return "1M";
    }
  }, [settings?.preferences?.performancePeriod]);
  const [timeFilter, setTimeFilter] = useState(() => getDefaultTimeFilter());

  const priceHistoryInterval = useMemo(() => {
    switch (timeFilter) {
      case "1D":
        return "1d";
      case "1W":
        return "1w";
      case "1M":
        return "1m";
      case "6M":
        return "6m";
      case "1Y":
        return "1y";
      default:
        return "1m";
    }
  }, [timeFilter]);

  // Get asset display information
  const assetDisplay = useMemo(() => {
    if (!selectedAsset) return { name: "", symbol: "", image: "" };

    return {
      name:
        selectedAsset?.assetName ||
        selectedAsset?.info?.assetName ||
        selectedAsset?.symbol?.toUpperCase() ||
        "",
      symbol:
        selectedAsset?.symbol?.toUpperCase() ||
        selectedAsset?.asset?.toUpperCase() ||
        "",
      image: selectedAsset?.image || selectedAsset?.info?.image || "",
    };
  }, [selectedAsset]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleDeposit = () => {
    navigate("/trading/digitalWallet", {
      state: {
        tabActive: "Deposit",
        selectedTab: "deposit",
        selectedCrypto: assetDisplay.symbol === "ZAR" ? null : selectedAsset,
        type: "withdraw-deposit",
      },
    });
  };

  const handleWithdraw = () => {
    navigate("/trading/digitalWallet", {
      state: {
        tabActive: "Withdraw",
        selectedTab: "withdraw",
        selectedCrypto: assetDisplay.symbol === "ZAR" ? null : selectedAsset,
        type: "withdraw-deposit",
      },
    });
  };

  const handleBuy = () => {
    navigate("/trading/digitalWallet", {
      state: {
        tabActive: "Buy",
        selectedTab: "Buy",
        selectedCrypto: assetDisplay.symbol === "ZAR" ? null : selectedAsset,
        type: "buy-sell",
      },
    });
  };

  useEffect(() => {
    if (pairObject) {
      setPairObject(pairObject);
    }
  }, [pairObject, setPairObject]);

  // Find market data for selected asset
  const getMarketData = () => {
    if (!marketAssets || !selectedAsset) return null;

    // Try multiple ways to get the asset symbol
    const assetSymbol =
      selectedAsset?.symbol?.toUpperCase() ||
      selectedAsset?.asset?.toUpperCase() ||
      (selectedAsset?.info?.asset && selectedAsset.info.asset.toUpperCase());

    if (!assetSymbol) return null;

    const entry = Object.entries(marketAssets).find(
      ([k]) => k.toUpperCase() === assetSymbol
    );
    let marketData = entry ? entry[1] : null;

    if (!marketData) {
      marketData = Object.values(marketAssets).find(
        (data) =>
          data.symbol?.toUpperCase() === assetSymbol ||
          data.info?.asset?.toUpperCase() === assetSymbol
      );
    }

    if (!marketData) return null;

    return {
      marketCap: marketData.market_cap || 0,
      volume24h: marketData.volume_24h || 0,
      price: marketData.price || 0,
      percentChange24h: marketData.percent_change_24h ?? 0,
      percentChange1h: marketData.percent_change_1h ?? 0,
      percentChange7d: marketData.percent_change_7d ?? 0,
      percentChange30d: marketData.percent_change_30d ?? 0,
      volumeChange24h: marketData.volume_change_24h ?? 0,
      marketCapDominance: marketData.market_cap_dominance ?? 0,
      maxSupply: marketData.max_supply ?? null,
      totalSupply: marketData.total_supply ?? null,
      circulatingSupply: marketData.circulating_supply ?? null,
      allTimeHigh: marketData.ath ?? null,
      athPercentage: marketData.ath_percentage ?? null,
      rank: marketData.market_cap_rank ?? null,
      description: marketData.info?.cmc?.description ?? null,
      lastUpdated: marketData.last_updated ?? null,
    };
  };

  const marketData = getMarketData();

  const {
    data: priceHistoryData,
    isLoading: isLoadingPriceHistory,
    isFetching: isFetchingPriceHistory,
  } = usePriceHistory(assetDisplay.symbol, priceHistoryInterval);

  const displayPriceAndChange = useMemo(() => {
    const prices = priceHistoryData?.prices;
    if (prices?.length > 0) {
      const firstPrice = prices[0].price;
      const lastPrice = prices[prices.length - 1].price;
      const changeAmount = lastPrice - firstPrice;
      const changePercent =
        firstPrice !== 0 ? (changeAmount / firstPrice) * 100 : 0;
      return {
        price: lastPrice,
        changeAmount,
        changePercent,
      };
    }
    if (marketData?.price != null && marketData?.percentChange24h != null) {
      return {
        price: marketData.price,
        changeAmount: marketData.price * (marketData.percentChange24h / 100),
        changePercent: marketData.percentChange24h,
      };
    }
    if (marketData?.price != null) {
      return {
        price: marketData.price,
        changeAmount: null,
        changePercent: null,
      };
    }
    return { price: null, changeAmount: null, changePercent: null };
  }, [
    priceHistoryData?.prices,
    marketData?.price,
    marketData?.percentChange24h,
  ]);

  const assetIdentifiers = useMemo(() => {
    if (!selectedAsset) return [];
    const identifiers = new Set();
    if (selectedAsset.symbol)
      identifiers.add(selectedAsset.symbol.toUpperCase());
    if (selectedAsset.asset) identifiers.add(selectedAsset.asset.toUpperCase());
    if (selectedAsset.info?.asset)
      identifiers.add(selectedAsset.info.asset.toUpperCase());
    return Array.from(identifiers);
  }, [selectedAsset]);

  const assetBalance = useMemo(() => {
    if (
      !walletAssetsData?.assets ||
      !selectedAsset ||
      assetIdentifiers.length === 0
    ) {
      return { balance: 0, valueInZar: 0 };
    }

    const asset = walletAssetsData.assets.find((a) => {
      const assetKey = a.asset?.toUpperCase() || "";
      return assetIdentifiers.includes(assetKey);
    });

    return {
      balance: asset?.availableAmount || 0,
      valueInZar: asset?.valueInZar || 0,
    };
  }, [walletAssetsData, selectedAsset, assetIdentifiers]);

  const assetTransactions = useMemo(() => {
    if (
      !transactionsComplete ||
      !selectedAsset ||
      assetIdentifiers.length === 0
    ) {
      return [];
    }

    return transactionsComplete.filter((tx) => {
      const txAsset = (tx.asset || tx.currency || "").toUpperCase();
      return assetIdentifiers.includes(txAsset);
    });
  }, [transactionsComplete, selectedAsset, assetIdentifiers]);

  // eslint-disable-next-line no-unused-vars -- used in Portfolio metrics JSX below
  const portfolioMetrics = useMemo(() => {
    if (
      !transactionsComplete ||
      !selectedAsset ||
      assetIdentifiers.length === 0 ||
      assetTransactions.length === 0
    ) {
      return {
        averageCost: 0,
        netInvested: 0,
        unrealizedPnL: 0,
        portfolioDiversity: 0,
        totalPnL: 0,
        pnlPercentage: 0,
        totalDeposited: 0,
        totalWithdrawn: 0,
      };
    }

    let totalBuyAmount = 0; // Total crypto bought
    let totalBuyCost = 0; // Total cost in ZAR
    let totalSellAmount = 0; // Total crypto sold
    let totalSellValue = 0; // Total value from sells in ZAR

    assetTransactions.forEach((tx) => {
      const subtype = tx.transactionSubtype?.toLowerCase();
      const amount = parseFloat(
        tx.amount ||
          tx.transactionRef?.total?.$numberDecimal ||
          tx.transactionRef?.totalAmount?.$numberDecimal ||
          0
      );

      let zarValue = 0;
      if (tx.transactionRef?.amount?.$numberDecimal) {
        zarValue = parseFloat(tx.transactionRef.amount.$numberDecimal);
      } else if (tx.valueInZar) {
        zarValue =
          typeof tx.valueInZar === "object" && tx.valueInZar.$numberDecimal
            ? parseFloat(tx.valueInZar.$numberDecimal)
            : parseFloat(tx.valueInZar);
      } else if (tx.transactionRef?.price?.$numberDecimal) {
        zarValue = amount * parseFloat(tx.transactionRef.price.$numberDecimal);
      }

      if (
        subtype === "buy" ||
        subtype === "deposit" ||
        subtype === "incoming"
      ) {
        totalBuyAmount += amount;
        totalBuyCost += zarValue || 0;
      } else if (
        subtype === "sell" ||
        subtype === "withdrawal" ||
        subtype === "outgoing"
      ) {
        totalSellAmount += amount;
        totalSellValue += zarValue || 0;
      }
    });

    const currentBalance = assetBalance.balance;
    const netAmount = totalBuyAmount - totalSellAmount;

    const averageBuyPrice =
      totalBuyAmount > 0 ? totalBuyCost / totalBuyAmount : 0;

    const realizedPnL = totalSellValue - averageBuyPrice * totalSellAmount;
    const netInvested = totalBuyCost - averageBuyPrice * totalSellAmount;

    const averageCost =
      netAmount > 0 ? netInvested / netAmount : averageBuyPrice;

    const currentValue = assetBalance.valueInZar || 0;
    const unrealizedPnL = currentValue - averageCost * currentBalance;
    const totalPnL = unrealizedPnL + realizedPnL;
    const pnlPercentage = netInvested > 0 ? (totalPnL / netInvested) * 100 : 0;

    // Calculate portfolio diversity
    const totalPortfolioValue = walletAssetsData?.zarEstimate || 0;
    const portfolioDiversity =
      totalPortfolioValue > 0 ? (currentValue / totalPortfolioValue) * 100 : 0;

    return {
      averageCost: averageCost || 0,
      netInvested: netInvested || 0,
      unrealizedPnL: unrealizedPnL || 0,
      portfolioDiversity: portfolioDiversity || 0,
      totalPnL: totalPnL || 0,
      pnlPercentage: pnlPercentage || 0,
      totalDeposited: totalBuyCost || 0,
      totalWithdrawn: totalSellValue || 0,
    };
  }, [
    transactionsComplete,
    selectedAsset,
    assetIdentifiers,
    assetBalance,
    walletAssetsData,
    assetTransactions,
  ]);

  // Format balance display
  const balanceDisplay = useMemo(() => {
    const symbol =
      selectedAsset?.symbol ||
      selectedAsset?.asset ||
      selectedAsset?.info?.asset ||
      "ETH";
    const assetName =
      selectedAsset?.assetName ||
      selectedAsset?.info?.assetName ||
      symbol.toUpperCase();
    const balance = assetBalance.balance;

    const formattedAmount = formatter.formatCrypto(
      balance,
      symbol.toUpperCase()
    );

    return {
      symbol: symbol.toUpperCase(),
      assetName: assetName,
      balance: balance,
      formattedBalance: formattedAmount,
      valueInZar: assetBalance.valueInZar,
    };
  }, [assetBalance, selectedAsset, formatter]);

  const isLoadingBalance = isLoadingWallet || isLoadingTransactions;
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [assetDisplay.image]);

  const chartDataFromApi = useMemo(() => {
    const prices = priceHistoryData?.prices;
    if (!prices?.length) return null;
    const labelFormat =
      timeFilter === "1D"
        ? "HH:mm"
        : timeFilter === "1W"
          ? "ddd"
          : timeFilter === "1M"
            ? "MMM D"
            : timeFilter === "6M" || timeFilter === "1Y"
              ? "MMM YY"
              : "MMM";
    const labels = prices.map((p) =>
      dayjs(p.time ?? p.timestamp).format(labelFormat)
    );
    const dataPoints = prices.map((p) => p.price);
    return { labels, dataPoints };
  }, [priceHistoryData?.prices, timeFilter]);

  const chartData = useMemo(() => {
    const labels = chartDataFromApi?.labels ?? [];
    const dataPoints = chartDataFromApi?.dataPoints ?? [];
    const pointCount = dataPoints.length;
    return {
      labels,
      datasets: [
        {
          label: "Price (USD)",
          data: dataPoints,
          fill: true,
          borderColor: "#17C3A5",
          backgroundColor: "rgba(20, 184, 166, 0.1)",
          tension: 0.4,
          pointRadius: pointCount > 0 && pointCount <= 14 ? 3 : 0,
          pointHoverRadius: 5,
          borderWidth: 2,
        },
      ],
    };
  }, [chartDataFromApi]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          titleColor: "#1F2937",
          bodyColor: "#1F2937",
          borderColor: "#E5E7EB",
          borderWidth: 1,
          padding: 12,
          titleFont: {
            size: 12,
            weight: "normal",
          },
          bodyFont: {
            size: 12,
            weight: "normal",
          },
          displayColors: false,
          callbacks: {
            label: (context) => {
              if (settings?.digitalWallet?.showBalances === false) {
                return "****";
              }
              const value = context.parsed.y;
              const formatted =
                typeof value === "number" && Number.isFinite(value)
                  ? value.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 8,
                    })
                  : String(value);
              return `$ ${formatted.replace(/,/g, " ")}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: "#9D96AF",
            font: {
              size: 12,
              weight: "normal",
            },
          },
        },
        y: {
          beginAtZero: false,
          grid: {
            display: true,
            color: "rgba(0, 0, 0, 0.05)",
          },
          ticks: {
            color: "#9D96AF",
            callback: (value) => {
              const numValue = Number(value);
              if (!Number.isFinite(numValue)) return "";
              if (numValue >= 1e6) {
                return formatWithThousandSpaces(numValue, 0);
              }
              if (numValue >= 1 || numValue <= -1) {
                return formatWithThousandSpaces(numValue, 2);
              }
              if (numValue > 0 || numValue < 0) {
                return truncateDecimals(numValue, 4);
              }
              return "0";
            },
            font: {
              size: 12,
              weight: "normal",
            },
          },
        },
      },
    }),
    [settings?.digitalWallet?.showBalances]
  );

  return (
    <div className="bg-gray-100 min-h-screen">
      <Header />
      <Toaster />

      <div className="px-3 lg:px-14 py-6 bg-bg-light-gray rounded-t-2xl min-h-[calc(100vh-100px)] space-y-5">
        <div className="flex justify-center items-center gap-2 h-[52px] rounded-2xl bg-portfolio-summary block md:hidden">
          <img src={fundsIcon} alt="Funds" className="w-[16px] h-[16px]" />
          <p className="text-[16px] font-normal text-[#EBE9EF]">Portfolio</p>
          <p className="text-[16px] font-semibold text-white">R803,490.32</p>
        </div>
        <div className="flex md:hidden border-[1px] border-[#F1EDF7] rounded-2xl px-4 py-2 bg-[#EBE9EF4D] gap-3">
          <div className="flex items-center gap-2">
            <span className="text-[#6D6584] text-sm font-semibold">Assets</span>
          </div>
          <div className="flex gap-2 overflow-x-auto hide-scrollbar">
            {isLoadingWallet ? (
              <div className="flex gap-2">
                {[...Array(3)].map((_, idx) => (
                  <div
                    key={idx}
                    className="flex-shrink-0 h-[32px] bg-gray-200 rounded-full px-3 flex items-center gap-2 animate-pulse"
                    style={{ minWidth: "120px" }}
                  >
                    <div className="w-[16px] h-[16px] rounded-full bg-gray-300"></div>
                    <div className="h-4 w-16 bg-gray-300 rounded"></div>
                  </div>
                ))}
              </div>
            ) : walletAssetsData?.assets &&
              walletAssetsData.assets.length > 0 ? (
              (() => {
                // Separate ZAR and other assets, sort by value
                const zarAsset = walletAssetsData.assets.find(
                  (a) => a.asset?.toLowerCase() === "zar"
                );
                const otherAssets = walletAssetsData.assets
                  .filter((a) => a.asset?.toLowerCase() !== "zar")
                  .filter((a) => {
                    const amount = parseFloat(a.availableAmount) || 0;
                    return amount > 0;
                  })
                  .sort((a, b) => {
                    const valueA = parseFloat(a.valueInZar) || 0;
                    const valueB = parseFloat(b.valueInZar) || 0;
                    return valueB - valueA;
                  });

                const sortedAssets = zarAsset
                  ? [zarAsset, ...otherAssets]
                  : otherAssets;

                return sortedAssets.map((asset, index) => {
                  const assetSymbol = asset.asset?.toUpperCase() || "";
                  const balance = parseFloat(asset.availableAmount) || 0;
                  const isZar = assetSymbol === "ZAR";

                  // Format display value
                  let displayValue = "";
                  if (isZar) {
                    displayValue = formatter.formatFiat(balance, "ZAR");
                  } else {
                    displayValue = formatter.formatCrypto(balance, assetSymbol);
                  }

                  return (
                    <AssetPill
                      key={asset.asset || index}
                      assetSymbol={assetSymbol}
                      image={asset.image}
                      displayValue={displayValue}
                    />
                  );
                });
              })()
            ) : (
              <div className="text-sm text-[#6D6584] py-2">
                No assets available
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col lg:flex-row justify-between space-y-4 lg:space-y-0 lg:!mt-2 !mt-6 mb-8">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="w-[48px] h-[48px] rounded-full border border-[#EBE9EF] flex items-center justify-center transition-colors flex-shrink-0"
              aria-label="Go back"
            >
              <ArrowLeft className="h-5 w-5 text-[#6D6584]" />
            </button>
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="lg:w-[52px] lg:h-[52px] w-[46px] h-[46px] rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center">
                {assetDisplay.image && !imageError ? (
                  <img
                    src={assetDisplay.image}
                    alt={assetDisplay.name}
                    className="w-full h-full object-cover"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400 rounded-full">
                    <span className="text-sm font-semibold">
                      {assetDisplay.symbol?.charAt(0) || "?"}
                    </span>
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <h1 className="lg:text-[32px] text-[24px] font-semibold text-[#423E51] truncate">
                  {assetDisplay.name === "South African Rand"
                    ? "Local Currency"
                    : assetDisplay.name || "Asset"}
                </h1>
                <p className="lg:text-[20px] text-[16px] text-[#6D6584] truncate font-semibold">
                  {assetDisplay.symbol || ""}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {
              assetDisplay.symbol === "ZAR" ? (
                <Button
                  onClick={handleWithdraw}
                  variant="outline"
                  className="h-[48px] px-6 w-[50%] lg:w-auto rounded-full border border-[#EBE9EF] text-[#6D6584] font-semibold text-[14px] bg-transparent flex items-center gap-2"
                >
                  <img
                    src={alertIcon}
                    alt="Alert"
                    className="h-[16px] w-[16px]"
                  />
                  <span>Withdraw</span>
                </Button>
              ) : null
              // <Button
              //   onClick={handleSetAlert}
              //   variant="outline"
              //   className="h-[48px] px-6 w-[50%] lg:w-auto rounded-full border border-[#EBE9EF] text-[#6D6584] font-semibold text-[14px] bg-transparent flex items-center gap-2"
              // >
              //   <img
              //     src={alertIcon}
              //     alt="Alert"
              //     className="h-[16px] w-[16px]"
              //   />
              //   <span>Set Alert</span>
              // </Button>
            }
            <Button
              onClick={handleDeposit}
              variant="outline"
              className="h-[48px] px-6 w-[50%] lg:w-auto rounded-full border border-[#EBE9EF] text-[#6D6584] font-semibold text-[14px] bg-transparent flex items-center gap-2"
            >
              <img
                src={depositIcon}
                alt="Deposit"
                className="h-[16px] w-[16px]"
              />
              <span>Deposit</span>
            </Button>
          </div>
        </div>
        {assetDisplay.symbol !== "ZAR" && (
          <>
            <div className="flex flex-wrap lg:flex-nowrap lg:space-x-5 space-x-0 lg:gap-0 gap-4">
              <div className="w-full lg:w-[60%] border-[1px] border-[#EBE9EF] rounded-2xl lg:px-[30px] px-4 pt-[26px] pb-[32px] bg-white flex flex-col">
                <div className="lg:order-0 order-1">
                  <p className="text-[16px] font-semibold text-[#6D6584]">
                    Market Price
                  </p>
                  <div className="text-[36px] font-semibold text-[#33125F] flex lg:items-center items-start flex-col lg:flex-row lg:gap-5 gap-0">
                    {isLoadingPriceHistory || isFetchingPriceHistory ? (
                      <Skeleton className="h-9 w-32 bg-gray-200" />
                    ) : displayPriceAndChange.price != null ? (
                      <p>$ {formatWithThousandSpaces(marketData?.price, 4)}</p>
                    ) : (
                      <Skeleton className="h-9 w-32 bg-gray-200" />
                    )}
                    {!(isLoadingPriceHistory || isFetchingPriceHistory) &&
                    displayPriceAndChange.changePercent != null &&
                    displayPriceAndChange.changeAmount != null ? (
                      <div
                        className={`flex items-center gap-3 text-[14px] font-semibold ${
                          displayPriceAndChange.changePercent >= 0
                            ? "text-[#17C3A5]"
                            : "text-red-500"
                        }`}
                      >
                        <span>
                          {displayPriceAndChange.changeAmount >= 0 ? "+ " : ""}$
                          {formatWithThousandSpaces(
                            displayPriceAndChange.changeAmount,
                            4
                          )}
                        </span>
                        <span>
                          ({displayPriceAndChange.changePercent >= 0 ? "+" : ""}
                          {truncateDecimals(
                            displayPriceAndChange.changePercent,
                            2
                          )}
                          %)
                        </span>
                      </div>
                    ) : null}
                  </div>
                </div>
                <ToggleGroup
                  type="single"
                  value={timeFilter}
                  onValueChange={(value) => value && setTimeFilter(value)}
                  className="md:border md:border-[1px] md:border-[#EBE9EF] md:rounded-full h-[48px] p-1 gap-5 md:gap-1 lg:w-fit w-full lg:mt-4 mt-0 lg:mb-0 mb-4 lg:order-1 order-0"
                >
                  {timeFilters.map((filter) => (
                    <ToggleGroupItem
                      value={filter}
                      key={filter}
                      className={`rounded-full px-6 py-1 text-sm font-medium transition-all min-w-[50px] ${filter === "1D" || filter === "1W" ? "lg:w-auto w-[30%]" : "hidden md:block"} ${
                        timeFilter === filter
                          ? "bg-[#33125F] hover:bg-[#33125F] hover:text-white text-white shadow-sm"
                          : "bg-transparent text-gray-600 border border-[1px] border-[#EBE9EF] md:border-none"
                      }`}
                    >
                      <span className="md:hidden">{filter}</span>
                      <span className="hidden md:inline">{filter}</span>
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
                <div className="relative mt-6 flex-1 min-h-[300px] order-2">
                  {(isLoadingPriceHistory || isFetchingPriceHistory) &&
                  !chartDataFromApi?.dataPoints?.length ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-50/80 rounded-lg">
                      <Skeleton className="h-full w-full max-h-[300px] bg-gray-200" />
                    </div>
                  ) : null}
                  <Line
                    key={`${assetDisplay.symbol}-${timeFilter}`}
                    data={chartData}
                    options={chartOptions}
                  />
                </div>
              </div>
              <div className="w-full lg:w-[40%]">
                <TradingInterface selectedAsset={selectedAsset} />
              </div>
            </div>
            <div className="flex flex-wrap lg:flex-nowrap lg:space-x-5 space-x-0 lg:gap-0 gap-4">
              <div className="w-full lg:w-1/2 border-[1px] border-[#EBE9EF] rounded-2xl px-[30px] pt-[26px] pb-[32px] bg-white space-y-3">
                <p className="text-[20px] font-semibold text-[#6D6584]">
                  Description
                </p>
                {marketData?.description ? (
                  <p className="text-[16px] text-[#423E51]">
                    {marketData.description}
                  </p>
                ) : (
                  <p className="text-[16px] text-[#423E51]">
                    No description available for this asset.
                  </p>
                )}
                <img
                  src={coinMarketCap}
                  className="h-[35px] w-auto"
                  alt="CoinMarketCap"
                />
              </div>
              <div className="w-full lg:w-1/2 border-[1px] border-[#EBE9EF] rounded-2xl px-[30px] pt-[26px] pb-[32px] bg-white">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-[20px] font-semibold text-[#6D6584]">
                    Market Data
                  </p>
                </div>

                {marketData ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                    <div className="md:order-1">
                      <p className="text-[14px] text-[#6D6584] mb-1">
                        Market Cap
                      </p>
                      <p className="text-[16px] font-semibold text-[#423E51]">
                        {formatMarketCap(marketData.marketCap)}
                      </p>
                    </div>
                    <div className="md:order-2">
                      <p className="text-[14px] text-[#6D6584] mb-1">
                        24h Volume
                      </p>
                      <p className="text-[16px] font-semibold text-[#423E51]">
                        {formatVolume(marketData.volume24h)}
                      </p>
                    </div>
                    <div className="md:order-3">
                      <p className="text-[14px] text-[#6D6584] mb-1">
                        Circulating
                      </p>
                      <p className="text-[16px] font-semibold text-[#423E51]">
                        {marketData.circulatingSupply
                          ? formatSupply(
                              marketData.circulatingSupply,
                              selectedAsset?.symbol || "ETH"
                            )
                          : "—"}
                      </p>
                    </div>
                    <div className="md:order-4">
                      <p className="text-[14px] text-[#6D6584] mb-1">Rank</p>
                      <p className="text-[16px] font-semibold text-[#423E51]">
                        {marketData.rank ? `#${marketData.rank}` : "—"}
                      </p>
                    </div>
                    <div className="md:order-5">
                      <p className="text-[14px] text-[#6D6584] mb-1">
                        Max Supply
                      </p>
                      <p className="text-[16px] font-semibold text-[#423E51]">
                        {marketData.maxSupply
                          ? formatSupply(
                              marketData.maxSupply,
                              selectedAsset?.symbol || "ETH"
                            )
                          : "—"}
                      </p>
                    </div>
                    <div className="md:order-6">
                      <p className="text-[14px] text-[#6D6584] mb-1">
                        Total Supply
                      </p>
                      <p className="text-[16px] font-semibold text-[#423E51]">
                        {marketData.totalSupply
                          ? formatSupply(
                              marketData.totalSupply,
                              selectedAsset?.symbol || "ETH"
                            )
                          : "—"}
                      </p>
                    </div>
                    <div className="md:order-7">
                      <p className="text-[14px] text-[#6D6584] mb-1">
                        All-Time High
                      </p>
                      <p className="text-[16px] font-semibold text-[#423E51]">
                        {marketData.allTimeHigh
                          ? formatPrice(marketData.allTimeHigh)
                          : "—"}
                      </p>
                    </div>
                    <div className="md:order-8">
                      <p className="text-[14px] text-[#6D6584] mb-1">
                        % of ATH
                      </p>
                      <p
                        className={`text-[16px] font-semibold ${
                          marketData.athPercentage != null
                            ? Number(marketData.athPercentage) < 0
                              ? "text-red-500"
                              : "text-green-500"
                            : "text-[#423E51]"
                        }`}
                      >
                        {marketData.athPercentage != null
                          ? `${Number(marketData.athPercentage) >= 0 ? "+" : ""}${Number(marketData.athPercentage).toFixed(2)}%`
                          : marketData.allTimeHigh
                            ? formatPercentOfATH(
                                marketData.price,
                                marketData.allTimeHigh
                              )
                            : "—"}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-[#6D6584]">
                    <p>Market data not available</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
        <div className="flex flex-wrap lg:flex-nowrap lg:space-x-5 space-x-0 lg:gap-0 gap-4">
          {!isLoadingBalance &&
          (assetBalance.balance === 0 || assetBalance.balance === null) ? (
            <div className="w-full lg:w-[35%] lg:px-[30px] px-4 pt-[26px] pb-[32px] bg-no-funds-asset-dashboard flex flex-col items-center justify-between lg:min-h-[672px] min-h-[474px]">
              <div className="text-center lg:mb-10 mb-8 mt-8">
                <p className="text-[16px] text-[#6D6584]">My Balance</p>
                <p className="text-[32px] font-semibold text-[#423E51]">
                  {balanceDisplay.formattedBalance}
                </p>
              </div>
              <div className="flex flex-col items-center justify-center space-y-4">
                <p className="text-[#33125F] text-[24px] font-semibold text-center max-w-[280px] mb-6">
                  No {selectedAsset?.symbol?.toUpperCase()} <br />
                  Balance Available
                </p>
                <Button
                  onClick={handleBuy}
                  className="border border-[#EBE9EF] text-[#6D6584] text-[14px] font-semibold rounded-full h-[48px] px-6 bg-transparent flex items-center gap-2 hover:bg-gray-50"
                >
                  Buy {selectedAsset?.symbol?.toUpperCase()}
                </Button>
              </div>
              <div></div>
            </div>
          ) : (
            <>
              {assetDisplay.symbol === "ZAR" ? (
                <div
                  className="w-full lg:w-[35%] border-[1px] border-[#EBE9EF] rounded-2xl lg:px-[30px] px-4 pt-[26px] pb-[72px] bg-white space-y-3 
              bg-funds-asset-dashboard"
                >
                  <div className="text-center text-[#EBE9EF] lg:mb-10 mb-8">
                    <p className="text-[20px] font-semibold">My Balance</p>
                    {isLoadingBalance ? (
                      <div className="space-y-2">
                        <Skeleton className="h-8 w-32 mx-auto bg-gray-300" />
                      </div>
                    ) : (
                      <>
                        <p className="text-[32px] font-semibold">
                          {formatter.formatFiat(
                            assetBalance.valueInZar || 0,
                            "ZAR"
                          )}
                        </p>
                      </>
                    )}
                  </div>
                  <div className="text-[#EBE9EF] space-y-6">
                    <div
                      className="p-[25px] rounded-2xl"
                      style={{
                        background:
                          "radial-gradient(196.29% 458.78% at -8.86% -12.86%, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0) 100%)",
                      }}
                    >
                      <p className="text-[16px]">Total Deposited</p>
                      {isLoadingBalance || isLoadingEstimate ? (
                        <Skeleton className="h-6 w-24 bg-gray-300 mt-2" />
                      ) : (
                        <p className="text-[24px] font-semibold">
                          {formatter.formatFiat(
                            portfolioEstimateData?.totalDepositInZar ?? 0,
                            "ZAR"
                          )}
                        </p>
                      )}
                    </div>
                    <div
                      className="p-[25px] rounded-2xl"
                      style={{
                        background:
                          "radial-gradient(196.29% 458.78% at -8.86% -12.86%, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0) 100%)",
                      }}
                    >
                      <p className="text-[16px]">Total Withdrawn</p>
                      {isLoadingBalance || isLoadingEstimate ? (
                        <Skeleton className="h-6 w-24 bg-gray-300 mt-2" />
                      ) : (
                        <p className="text-[24px] font-semibold">
                          {formatter.formatFiat(
                            portfolioEstimateData?.totalWithdrawInZar ?? 0,
                            "ZAR"
                          )}
                        </p>
                      )}
                    </div>
                    <div
                      className="p-[25px] rounded-2xl"
                      style={{
                        background:
                          "radial-gradient(196.29% 458.78% at -8.86% -12.86%, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0) 100%)",
                      }}
                    >
                      <p className="text-[16px]">Portfolio Diversity</p>
                      {isLoadingBalance || isLoadingEstimate ? (
                        <Skeleton className="h-6 w-24 bg-gray-300 mt-2" />
                      ) : (
                        <p className="text-[24px] font-semibold">
                          {(
                            portfolioEstimateData?.portfolioDiversity ?? 0
                          ).toFixed(2)}
                          %
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="w-full lg:w-[35%] border-[1px] border-[#EBE9EF] rounded-2xl lg:px-[30px] px-4 pt-[26px] pb-[32px] bg-white space-y-3 
              bg-funds-asset-dashboard"
                >
                  <div className="text-center text-[#EBE9EF] lg:mb-10 mb-8">
                    <p className="text-[20px] font-semibold">My Balance</p>
                    {isLoadingBalance ? (
                      <div className="space-y-2">
                        <Skeleton className="h-8 w-32 mx-auto bg-gray-300" />
                        <Skeleton className="h-4 w-24 mx-auto bg-gray-300" />
                      </div>
                    ) : (
                      <>
                        <p className="text-[32px] font-semibold">
                          {balanceDisplay.formattedBalance}
                        </p>
                        {/* <p
                          className={`text-[14px] font-normal ${
                            portfolioMetrics.pnlPercentage >= 0
                              ? "text-[#17C3A5]"
                              : "text-rose-500"
                          }`}
                        >
                          {portfolioMetrics.totalPnL >= 0 ? "+" : ""}
                          {formatter.formatFiat(
                            portfolioMetrics.totalPnL,
                            "ZAR"
                          )}{" "}
                          ({portfolioMetrics.pnlPercentage >= 0 ? "+" : ""}
                          {portfolioMetrics.pnlPercentage.toFixed(2)}%)
                        </p> */}
                      </>
                    )}
                  </div>
                  {/* <div className="text-[#EBE9EF] space-y-4">
                    <div
                      className="p-[25px] rounded-2xl"
                      style={{
                        background:
                          "radial-gradient(196.29% 458.78% at -8.86% -12.86%, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0) 100%)",
                      }}
                    >
                      <p className="text-[16px]">Average cost</p>
                      {isLoadingBalance ? (
                        <Skeleton className="h-6 w-24 bg-gray-300 mt-2" />
                      ) : (
                        <p className="text-[24px] font-semibold">
                          {portfolioMetrics.averageCost > 0
                            ? formatter.formatFiat(
                                portfolioMetrics.averageCost,
                                "ZAR"
                              )
                            : "—"}
                        </p>
                      )}
                    </div>
                    <div
                      className="p-[25px] rounded-2xl"
                      style={{
                        background:
                          "radial-gradient(196.29% 458.78% at -8.86% -12.86%, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0) 100%)",
                      }}
                    >
                      <p className="text-[16px]">Net Invested</p>
                      {isLoadingBalance ? (
                        <Skeleton className="h-6 w-24 bg-gray-300 mt-2" />
                      ) : (
                        <p className="text-[24px] font-semibold">
                          {formatter.formatFiat(
                            portfolioMetrics.netInvested,
                            "ZAR"
                          )}
                        </p>
                      )}
                    </div>
                    <div
                      className="p-[25px] rounded-2xl"
                      style={{
                        background:
                          "radial-gradient(196.29% 458.78% at -8.86% -12.86%, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0) 100%)",
                      }}
                    >
                      <p className="text-[16px]">Unrealised Profit & Loss</p>
                      {isLoadingBalance ? (
                        <Skeleton className="h-6 w-24 bg-gray-300 mt-2" />
                      ) : (
                        <p className={`text-[24px] font-semibold`}>
                          {portfolioMetrics.unrealizedPnL >= 0 ? "+" : ""}
                          {formatter.formatFiat(
                            portfolioMetrics.unrealizedPnL,
                            "ZAR"
                          )}
                        </p>
                      )}
                    </div>
                    <div
                      className="p-[25px] rounded-2xl"
                      style={{
                        background:
                          "radial-gradient(196.29% 458.78% at -8.86% -12.86%, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0) 100%)",
                      }}
                    >
                      <p className="text-[16px]">Portfolio Diversity</p>
                      {isLoadingBalance ? (
                        <Skeleton className="h-6 w-24 bg-gray-300 mt-2" />
                      ) : (
                        <p className="text-[24px] font-semibold">
                          {portfolioMetrics.portfolioDiversity.toFixed(2)}%
                        </p>
                      )}
                    </div>
                  </div> */}
                </div>
              )}
            </>
          )}
          {selectedAsset && userId && (
            <>
              {assetTransactions?.length > 0 ? (
                <TransactionHistoryAsset
                  userId={userId}
                  asset={selectedAsset}
                />
              ) : (
                <div className="relative flex flex-col items-center justify-center w-full lg:w-[65%] rounded-2xl border border-[#EBE9EF] p-6 bg-white gap-2 min-h-[406px]">
                  <p className="text-[20px] font-semibold absolute top-6 left-6 text-[#6D6584]">
                    Transaction History
                  </p>
                  <div className="flex flex-col items-center justify-center w-full mt-20 lg:mt-0 gap-2">
                    <img
                      src={sendCrypto}
                      alt="sendCrypto"
                      className="lg:w-[194px] lg:h-[136px] w-[137px] h-auto"
                    />
                    <p className="text-[#33125F] text-[24px] lg:text-left text-center lg:px-0 px-8 font-semibold">
                      Your crypto journey starts now
                    </p>
                    <p className="text-[#423E51] text-[14px] lg:text-left text-center lg:px-0 px-5 font-normal">
                      We couldn’t find any transactions for this account. New
                      activity will appear here.
                    </p>
                    <button
                      type="button"
                      onClick={handleBuy}
                      className="border border-[#EBE9EF] text-[#6D6584] text-[14px] font-semibold rounded-full h-[48px] lg:w-[150px] w-full mt-4 hover:bg-gray-50 transition-colors"
                    >
                      Buy {selectedAsset?.symbol?.toUpperCase()}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
